/**
 * Flow Store (useFlowStore)
 *
 * Zustand-based state management for the modal flow system.
 * This is a TRANSIENT, in-memory store - state is NOT persisted to localStorage.
 * Server is the source of truth; hydrate from API on initialization.
 *
 * KEY RESPONSIBILITIES:
 * - Track current flow type and step
 * - Accumulate data from each step
 * - Handle step transitions
 * - Manage navigation history for back functionality
 */
import type { GetOfferDto } from "@unreserved/sdk";
import { type StateCreator, create } from "zustand";
import { devtools } from "zustand/middleware";

import {
  FLOW_CONFIG,
  getFlowEntryPoint,
  getStepConfig,
  resolveNextStep,
} from "@/app/_components/flow-modal/flow-config";
import type {
  FlowData,
  FlowStore,
  FlowStoreState,
  FlowType,
  OnNextOptions,
} from "@/app/_components/flow-modal/flow-types";
import {
  createPropertyCardFromOffer,
  toCounterOffer,
  toOfferSummary,
} from "@/app/_libs/utils/private-sale-buyer";

// ============================================================================
// Initial State
// ============================================================================

const initialState: FlowStoreState = {
  isOpen: false,
  flowType: null,
  currentStepId: null,
  flowData: {},
  isLoading: false,
  error: null,
  history: [],
  customReason: "",
};

// ============================================================================
// Store Implementation
// ============================================================================

// Type helper to properly type the devtools middleware with FlowStore
type DevtoolsMiddleware = <T>(
  fn: StateCreator<T, [], []>,
  options?: { name?: string; enabled?: boolean },
) => StateCreator<T, [], []>;

export const useFlowStore = create<FlowStore>()(
  (devtools as DevtoolsMiddleware)(
    (set, get) => ({
      // Spread initial state
      ...initialState,

      // ========================================================================
      // Actions
      // ========================================================================

      /**
       * Start a new flow.
       * Opens the modal and initializes the flow from the entry point or specified step.
       */
      startFlow: (flowType: FlowType, initialStepId?: string, initialData: FlowData = {}) => {
        const flow = FLOW_CONFIG[flowType];
        if (!flow) {
          console.error(`[FlowStore] Unknown flow type: ${flowType}`);
          set({ error: `Unknown flow type: ${flowType}` });
          return;
        }

        const entryPoint = initialStepId ?? getFlowEntryPoint(flowType);
        const stepConfig = getStepConfig(flowType, entryPoint);

        if (!stepConfig) {
          console.error(`[FlowStore] Invalid step "${entryPoint}" for flow "${flowType}"`);
          set({ error: `Invalid step: ${entryPoint}` });
          return;
        }

        set({
          isOpen: true,
          flowType,
          currentStepId: entryPoint,
          flowData: initialData,
          error: null,
          history: [entryPoint],
        });
      },

      /**
       * Advance to the next step, accumulating data.
       * This is the primary transition function called by step components.
       *
       * Supports multiple navigation patterns:
       * 1. Config-based: Uses the step's `next` config (static, conditional, or dynamic)
       * 2. API-driven: Pass `apiResponse` to influence conditional/dynamic resolution
       * 3. Explicit: Pass `nextStepId` to override all resolution logic
       */
      nextStep: (options: OnNextOptions = {}) => {
        const { data: newData = {}, apiResponse, nextStepId: explicitNextId } = options;
        const { flowType, currentStepId, flowData, history } = get();

        if (!flowType || !currentStepId) {
          console.error("[FlowStore] Cannot advance: no active flow");
          return;
        }

        // 1. Accumulate data
        const updatedData = { ...flowData, ...newData };

        // 2. Get current step config
        const currentConfig = getStepConfig(flowType, currentStepId);
        if (!currentConfig) {
          console.error(`[FlowStore] Current step config not found: ${currentStepId}`);
          return;
        }

        // 3. Resolve next step ID
        let nextId: string | null;

        if (explicitNextId !== undefined) {
          // Explicit override from API - use directly
          nextId = explicitNextId;
        } else {
          // Resolve from config using context
          const resolverContext = {
            data: updatedData,
            apiResponse,
            currentStepId,
            flowType,
          };
          nextId = resolveNextStep(currentConfig.next, resolverContext);
        }

        if (nextId) {
          // 4. Validate next step exists
          const nextConfig = getStepConfig(flowType, nextId);
          if (!nextConfig) {
            console.error(`[FlowStore] Next step config not found: ${nextId}`);
            return;
          }

          // 5. Transition - update pointer and history
          set({
            currentStepId: nextId,
            flowData: updatedData,
            history: [...history, nextId],
          });

          // TODO: Optional - fire-and-forget sync to backend
          // api.updateProgress(flowType, nextId, updatedData);
        } else {
          // 6. Terminal step - just update data, flow stays open
          // Close behavior should be handled by the terminal component
          set({ flowData: updatedData });
        }
      },

      /**
       * Go back to the previous step using navigation history.
       */
      previousStep: () => {
        const { flowType, history, closeFlow } = get();

        if (!flowType || history.length <= 1) {
          // No history to go back to, closing modal
          closeFlow(true);
          return;
        }

        // Pop current step from history
        const newHistory = history.slice(0, -1);
        const previousStepId = newHistory[newHistory.length - 1];

        if (previousStepId) {
          set({
            currentStepId: previousStepId,
            history: newHistory,
          });
        }
      },

      /**
       * Jump directly to a specific step.
       * Use with caution - this bypasses normal flow logic.
       * Primarily for resuming from API hydration or error recovery.
       */
      goToStep: (stepId: string) => {
        const { flowType, history } = get();

        if (!flowType) {
          console.error("[FlowStore] Cannot go to step: no active flow");
          return;
        }

        const stepConfig = getStepConfig(flowType, stepId);
        if (!stepConfig) {
          console.error(`[FlowStore] Invalid step "${stepId}" for flow "${flowType}"`);
          return;
        }

        set({
          currentStepId: stepId,
          history: [...history, stepId],
        });
      },

      /**
       * Close the modal.
       * Optionally reset all state for a fresh start.
       */
      closeFlow: (reset = false) => {
        if (reset) {
          set(initialState);
        } else {
          set({ isOpen: false });
        }
      },

      /**
       * Full reset - returns store to initial state.
       */
      resetFlow: () => {
        set(initialState);
      },

      /**
       * Update flow data without changing steps.
       * Useful for partial updates or external data injection.
       */
      updateFlowData: (data: FlowData) => {
        const { flowData } = get();
        set({ flowData: { ...flowData, ...data } });
      },

      /**
       * PRIVATE_SALE_BUYER helper:
       * hydrate flow data from an offer API payload and start at a specific step.
       */
      startPrivateSaleBuyerFromOffer: (payload: Record<string, unknown>, initialStepId: string) => {
        const offerData = payload as unknown as GetOfferDto;

        const propertyCard = createPropertyCardFromOffer(offerData.listing);
        const offerSummary = toOfferSummary(offerData.offer);

        const counterOffer = toCounterOffer(offerData.offer);
        const originalOffer =
          offerData.originalOffer != null ? toCounterOffer(offerData.originalOffer) : undefined;

        const flowData: FlowData = {
          offerId: offerData.offer.id,
          propertyCard,
          offerSummary,
          counterOffer,
          originalOffer,
          offerDetails: offerData,
          paymentSummary: offerData.paymentSummary,
          isCounterOffer: offerData.offer.isCounterOffer ?? false,
        };

        // Use existing startFlow to initialize the buyer flow with this data
        get().startFlow("PRIVATE_SALE_BUYER", initialStepId, flowData);
      },

      /**
       * Hydrate store from API response.
       * Called during initialization to resume user's previous session.
       */
      hydrateFromApi: (flowType: FlowType, stepId: string, data: FlowData) => {
        const stepConfig = getStepConfig(flowType, stepId);
        if (!stepConfig) {
          console.error(`[FlowStore] Hydration failed - invalid step: ${stepId}`);
          return;
        }

        set({
          isOpen: true,
          flowType,
          currentStepId: stepId,
          flowData: data,
          isLoading: false,
          error: null,
          history: [stepId], // Start fresh history from hydrated step
        });
      },

      /**
       * Set loading state.
       */
      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      /**
       * Set error state.
       */
      setError: (error: string | null) => {
        set({ error });
      },

      /**
       * Set custom reason for rejection flows.
       */
      setCustomReason: (reason: string) => {
        set({ customReason: reason });
      },
    }),
    {
      name: "flow-store",
      enabled: process.env.NODE_ENV === "development",
    },
  ),
);

// ============================================================================
// Selectors (for performance optimization)
// ============================================================================

/**
 * Select only the data needed for rendering the current step.
 * Prevents unnecessary re-renders when other parts of state change.
 */
export const selectCurrentStep = (state: FlowStore) => ({
  flowType: state.flowType,
  currentStepId: state.currentStepId,
  stepConfig:
    state.flowType && state.currentStepId
      ? getStepConfig(state.flowType, state.currentStepId)
      : null,
});

/**
 * Select navigation-related state.
 */
export const selectNavigation = (state: FlowStore) => ({
  canGoBack: state.history.length > 1,
  historyLength: state.history.length,
});

/**
 * Select modal visibility state.
 */
export const selectModalState = (state: FlowStore) => ({
  isOpen: state.isOpen,
  isLoading: state.isLoading,
  error: state.error,
});
